import React, { useState, useEffect, useMemo } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ArrowDownWideNarrowIcon,
  ArrowUpWideNarrowIcon,
} from 'lucide-react';
import API from '../../services/api';
import {
  getMondayOfWeek,
  formatDate,
  convertUTCToLocal,
} from '../../utils/DateUtils';
import { useAuthContext } from '../auth/AuthProvider';
import TimeTrackingProjectItem from './TimeTrackingProjectItem';
import { permissions, hasPermission } from '../../config/permissions';
import { QUERY_KEYS } from '../../config/queryKey';
import { useQuery } from '@tanstack/react-query';
import Select from '../Select';
import Switch from '../Switch';

const ORDER_BY_OPTIONS = [
  {
    label: 'Project Name',
    value: 'project_code',
  },
  {
    label: 'Due Date',
    value: 'anticipated_end_date',
  },
];

const TimeTrackingContent = () => {
  const { user } = useAuthContext();

  const hasViewEmployee = hasPermission(
    user?.permissions || [],
    permissions.VIEW_ALL_EMPLOYEES,
  );

  const [activeOnly, setActiveOnly] = useState(false);

  const [selectedEmployee, setSelectedEmployee] = useState(user?.employee_id);
  const [selectedOrderBy, setSelectedOrderBy] = useState(
    ORDER_BY_OPTIONS[0].value,
  );
  const [orderDirection, setOrderDirection] = useState('ASC');

  const [currentDate, setCurrentDate] = useState(new Date());
  const [error, setError] = useState(null);

  // Format month for display
  const formatMonth = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      year: 'numeric',
    }).format(date);
  };

  const { data: employees = [], isLoading: isLoadingEmployees } = useQuery({
    queryKey: [QUERY_KEYS.GET_EMPLOYEES],
    queryFn: () => API.getEmployees(),
    enabled: hasViewEmployee,
  });

  const { data: projects = [], isLoading: isLoadingProjects } = useQuery({
    queryKey: [
      QUERY_KEYS.GET_PROJECTS,
      {
        employeeId: selectedEmployee,
        orderBy: selectedOrderBy,
        orderDirection: orderDirection,
      },
    ],
    queryFn: async () => {
      const projects = await API.getProjects(
        selectedEmployee,
        selectedOrderBy,
        orderDirection,
      );
      return projects;
    },
    enabled: !!selectedEmployee,
    onError: (error) => {
      setError(error?.message || 'Failed to load projects');
    },
  });

  const activeProjects = useMemo(() => {
    const statuses = activeOnly
      ? ['In Progress']
      : ['Not Started', 'In Progress', 'Delayed'];
    return projects.filter(
      (project) =>
        project?.deliverable_status &&
        statuses.includes(project.deliverable_status),
    );
  }, [projects, activeOnly]);

  const {
    data: monthlyHours = {},
    isLoading: isLoadingMonthlyData,
    refetch: refetchMonthlyData,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_MONTHLY_HOURS, selectedEmployee, currentDate],
    queryFn: async () => {
      const monthlyData = await API.getMonthlyHours(
        selectedEmployee,
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
      );

      const hoursMap = {};
      if (Array.isArray(monthlyData)) {
        monthlyData.forEach((entry) => {
          if (entry?.project_id && entry?.start_date) {
            const weekStart = getMondayOfWeek(
              convertUTCToLocal(entry.start_date),
            );

            const key = `${entry.project_id}`;
            if (!hoursMap[key]) {
              hoursMap[key] = {};
            }
            hoursMap[key][formatDate(weekStart)] = {
              ...entry,
              hours_worked: parseFloat(entry.hours_worked) || 0,
            };
          }
        });
      }
      return hoursMap;
    },
    enabled: !!selectedEmployee && !!currentDate,
    onError: (error) => {
      setError(error?.message || 'Failed to load monthly data');
    },
  });

  const handleEmployeeChange = (e) => {
    setSelectedEmployee(e.target.value);
  };

  useEffect(() => {
    if (user?.employee_id) {
      setSelectedEmployee(user?.employee_id);
    } else {
      setSelectedEmployee(null);
    }

    if (hasPermission(user?.permissions || [], permissions.VIEW_ALL_PROJECTS)) {
      setActiveOnly(false);
    } else {
      setActiveOnly(true);
    }
  }, [user]);

  const isLoading =
    isLoadingEmployees || isLoadingProjects || isLoadingMonthlyData;

  return (
    <div className="container mx-auto bg-white">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">Time Tracking</h1>
        <div className="flex items-center gap-4">
          <button
            onClick={() =>
              setCurrentDate(
                new Date(currentDate.setMonth(currentDate.getMonth() - 1)),
              )
            }
            className="p-2 rounded-lg border hover:bg-gray-50"
            disabled={isLoading}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-xl font-semibold">
            {formatMonth(currentDate)}
          </span>
          <button
            onClick={() =>
              setCurrentDate(
                new Date(currentDate.setMonth(currentDate.getMonth() + 1)),
              )
            }
            className="p-2 rounded-lg border hover:bg-gray-50"
            disabled={isLoading}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
      <div className="flex justify-between items-center mb-8">
        <div className="max-w-md">
          {hasViewEmployee && (
            <Select
              size="lg"
              options={[
                {
                  label: 'Select employee...',
                  value: '',
                },
                ...employees.map((employee) => ({
                  label: employee.name,
                  value: employee.employee_id,
                })),
              ]}
              value={selectedEmployee}
              onChange={handleEmployeeChange}
            />
          )}
        </div>

        <div className="flex flex-row items-center gap-4">
          <Switch
            label="Active Only"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          />

          <div className="max-w-md flex flex-row items-center gap-0">
            <button
              className="border border-gray-200 bg-gray-50 rounded-l-lg p-2 w-10 h-10 flex items-center justify-center"
              onClick={() =>
                setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC')
              }
            >
              {orderDirection === 'ASC' ? (
                <ArrowDownWideNarrowIcon className="w-4 h-4" />
              ) : (
                <ArrowUpWideNarrowIcon className="w-4 h-4" />
              )}
            </button>
            <Select
              options={ORDER_BY_OPTIONS}
              value={selectedOrderBy}
              onChange={(e) => setSelectedOrderBy(e.target.value)}
              selectedClassName="rounded-l-none border-l-0 h-10 bg-gray-50"
            />
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="text-center py-8">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg mb-8">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {selectedEmployee && (
        <div className="space-y-6">
          {(!activeProjects || activeProjects.length === 0) && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-gray-600">
                No active projects found for this employee.
              </p>
            </div>
          )}
          {activeProjects?.map((project, index) => (
            <TimeTrackingProjectItem
              key={project.project_id}
              project={project}
              currentDate={currentDate}
              monthlyHours={monthlyHours[project.project_id] || {}}
              employeeId={selectedEmployee}
              refetchMonthlyData={refetchMonthlyData}
              onCallback={() => {
                refetchMonthlyData();
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TimeTrackingContent;
